import { useEffect, useRef } from 'react';

/**
 * Hook to sync toolOutput changes to widgetState
 *
 * This hook correctly detects when toolOutput has ACTUALLY changed (not just on page reload)
 * and only then updates widgetState. This preserves user edits in widgetState across page reloads.
 *
 * Inspired by the shopping-cart example from OpenAI Apps SDK:
 * https://github.com/openai/openai-apps-sdk-examples/blob/main/src/shopping-cart/index.tsx
 *
 * @param toolOutput - The current toolOutput from window.openai
 * @param onToolOutputChange - Callback when toolOutput actually changes (not on every render)
 *
 * @example
 * ```tsx
 * const rawData = useWidgetProps<CalendarData>();
 * const [widgetState, setWidgetState] = useWidgetState({ posts: null });
 *
 * useToolOutputSync(rawData, (newData) => {
 *   // This only runs when toolOutput ACTUALLY changes
 *   // Not on page reload if toolOutput is the same
 *   setWidgetState({ posts: ensurePostsHaveIds(newData.posts) });
 * });
 *
 * // Use widgetState (with user edits) OR rawData (initial)
 * const posts = widgetState?.posts || rawData?.posts || [];
 * ```
 */
export function useToolOutputSync<T = unknown>(
  toolOutput: T | null | undefined,
  onToolOutputChange: (newToolOutput: T) => void
): void {
  const lastToolOutputRef = useRef<string>('__tool_output_unset__');

  useEffect(() => {
    // Skip if no toolOutput
    if (toolOutput == null) {
      if (import.meta.env.DEV) {
        console.log('[useToolOutputSync] Skipping - no toolOutput');
      }
      return;
    }

    // Full serialization of toolOutput to detect ACTUAL changes
    // This handles page reloads correctly - if toolOutput hasn't changed,
    // widgetState persists user edits
    const serializedToolOutput = (() => {
      try {
        return JSON.stringify(toolOutput);
      } catch (error) {
        console.warn('[useToolOutputSync] Unable to serialize toolOutput', error);
        return '__tool_output_error__';
      }
    })();

    // Skip if toolOutput hasn't actually changed
    // This is critical for preserving widgetState on page reload!
    if (serializedToolOutput === lastToolOutputRef.current) {
      if (import.meta.env.DEV) {
        console.log('[useToolOutputSync] Skipping - toolOutput unchanged (widgetState preserved)');
      }
      return;
    }

    if (import.meta.env.DEV) {
      console.log('[useToolOutputSync] toolOutput changed!', {
        previousHash: lastToolOutputRef.current.slice(0, 100) + '...',
        newHash: serializedToolOutput.slice(0, 100) + '...',
      });
    }

    // Update reference
    lastToolOutputRef.current = serializedToolOutput;

    // Call the callback with the new toolOutput
    onToolOutputChange(toolOutput as T);
  }, [toolOutput, onToolOutputChange]);
}
